import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import useDisclosure, { DisclosureHost } from "@/hooks/use-disclosure";
import { useQueryParams } from "@/hooks/use-query-param";
import { useGetAllScreeningPreview } from "@/queries/encounter/screening/use-get-all-screening-preview";
import { useGetAllScreenerType } from "@/queries/patient-chart/assessment/assement-forms/use-get-all-screener-type";
import {
  DEF_PAGINATION,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "@/utils/utils";
import { SCREENER_TYPE_PAGE_SIZE } from "../initial-f2f-code-constant";
import { CodeItem } from "../initial-f2f-types";

export interface UseScreeningSection {
  options: CodeItem[];
  isLoading: boolean;
  setSearchKey: (value: string) => void;
  screeners: object | undefined;
  hasScreeners: boolean;
  modal: DisclosureHost;
  openScreenings: (types: string[]) => void;
}

/** eCareHealth screener types to choose from, the screenings already answered for this appointment, and the Add Screening modal opened for each chosen type in turn. */
export const useScreeningSection = (): UseScreeningSection => {
  const appointmentId = useQueryParams("appointmentid");
  const [search, setSearchKey] = useDebounce<string>("");
  const modal = useDisclosure();
  const [queue, setQueue] = useState<string[]>([]);

  const { data: types, isFetching } = useGetAllScreenerType({
    pagination: { page: 0, pageSize: SCREENER_TYPE_PAGE_SIZE },
    xTENANTID: getXtenantId(),
    searchString: search,
  });

  const { data: preview } = useGetAllScreeningPreview({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    appointmentUuid: appointmentId ?? "",
    xTENANTID: getXtenantId(),
  });

  const { opened, open } = modal;
  useEffect(() => {
    if (opened || !queue.length) return;
    const [next, ...rest] = queue;
    setQueue(rest);
    open(next);
  }, [opened, open, queue]);

  const options = useMemo(
    () =>
      types?.data?.content?.flatMap((item: unknown) =>
        typeof item === "string"
          ? [{ value: item, code: replaceUnderScoreAddSpace(item) }]
          : []
      ) ?? [],
    [types]
  );

  const screeners = preview?.data;

  return useMemo(
    () => ({
      options,
      isLoading: isFetching,
      setSearchKey,
      screeners,
      hasScreeners: !!Object.keys(screeners ?? {}).length,
      modal,
      openScreenings: (picked: string[]) =>
        setQueue((prev) => [...prev, ...picked]),
    }),
    [options, isFetching, setSearchKey, screeners, modal]
  );
};
